/**
 * ProfileWindow — modal that opens when a user clicks a name in the
 * online list. Faithful to SWF `DefineSprite_223_profileWindow` frame 2:
 *
 *   ┌────────────────────────────────────────────────────────────┐
 *   │  username                                              [×] │
 *   ├──┬──────────────────────────────────────────────────────────┤
 *   │  │ Member:        ___       Games Played: ___              │
 *   │  │ Member for:    ___       AVG Rank:     ___              │
 *   │  │ GT:            ___       Rank:         ___              │
 *   │  │                                                          │
 *   │  │                          [BUZZ] [FORUM]                  │
 *   │  │     [pm text input          ]  [PM]  [PROFILE]           │
 *   │  └──────────────────────────────────────────────────────────┘
 *   │  ┌─ Moderator/Admin only ──────────────────────────────────┐
 *   │  │ [MUTE] [UN-MUTE] [KICK] [BAN]              [SLAP]       │
 *   │  │ [time field]                               [reason]     │
 *   │  └──────────────────────────────────────────────────────────┘
 *   └────────────────────────────────────────────────────────────┘
 *
 * Stats fetched from /prochat/api/profile?u=<userid>. The stats are
 * server-cached (60s) so repeated opens are cheap.
 */
import { useEffect, useState } from 'react';
import { Modal } from './Modal.js';
import { rankLabel } from '../lib/ranks.js';
import type { UserListEntry, UserView } from '../lib/types.js';

interface ProfileApi {
    userid: number;
    username: string | null;
    primary: number | null;
    memberfor: string | null;
    title: string | null;
    grandtotal: number | null;
    played: number | null;
    rank: number | null;
    avgrank: number | null;
}

interface Props {
    me: UserView;
    target: UserListEntry;
    room: string;
    onClose: () => void;
    onPm: (other: string) => void;
    onAction: (frame: object) => void;
    onC4Invite?: (name: string) => void;
}

const fmt = (n: number | null | undefined) => n == null ? '—' : n.toLocaleString();

export function ProfileWindow(props: Props) {
    const { me, target, room, onClose, onPm, onAction, onC4Invite } = props;
    const [data, setData]   = useState<ProfileApi | null>(null);
    const [err,  setErr]    = useState<string | null>(null);
    const [pmText, setPmText] = useState('');
    const [muteMins, setMuteMins] = useState(15);
    const [banMins,  setBanMins]  = useState(60);
    const [banReason,setBanReason]= useState('');

    useEffect(() => {
        let cancelled = false;
        const u = encodeURIComponent(target.id || target.name);
        fetch(`/prochat/api/profile?u=${u}`)
            .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
            .then((j: ProfileApi) => { if (!cancelled) setData(j); })
            .catch(e => { if (!cancelled) setErr(String(e?.message ?? e)); });
        return () => { cancelled = true; };
    }, [target.id, target.name]);

    const isSelf = target.id === me.id;
    const showMod = me.is_mod && !target.is_admin && !isSelf;
    const showAdmin = me.is_admin && !target.is_admin && !isSelf;

    const buzz = () => onAction({ cmd: 'ping_user', room, user: target.name });
    const slap = () => onAction({ cmd: 'slap_user', room, user: target.name });

    return (
        <Modal title={target.name} onClose={onClose} className="pc-modal-profile">
            <div className="pw-grid">
                <div className="pw-avatar">
                    {target.avatar
                        ? <img src={target.avatar} alt="" />
                        : <div className="pw-avatar-placeholder" />}
                </div>
                <div className="pw-stats">
                    <div><span>Member:</span>       <b style={{ color: target.color }}>{rankLabel(target.k)}</b></div>
                    <div><span>Member for:</span>   <b>{data?.memberfor ?? (err ? '—' : '…')}</b></div>
                    <div><span>GT:</span>           <b>{fmt(data?.grandtotal)}</b></div>
                    <div><span>Games Played:</span> <b>{fmt(data?.played)}</b></div>
                    <div><span>AVG Rank:</span>     <b>{fmt(data?.avgrank)}</b></div>
                    <div><span>Rank:</span>         <b>{fmt(data?.rank)}</b></div>
                    {data?.title && <div className="pw-title-row"><span>Title:</span> <b>{data.title}</b></div>}
                </div>
                <div className="pw-action-row">
                    <button className="pw-btn" onClick={buzz} disabled={isSelf} title="Send a buzz">BUZZ</button>
                    <a className="pw-btn" href={`/vbz/private.php?do=newpm&u=${target.id}`}
                       target="_blank" rel="noopener">FORUM PM</a>
                    <a className="pw-btn" href={`/profile/${encodeURIComponent(target.name)}/`}
                       target="_blank" rel="noopener">PROFILE</a>
                    {onC4Invite && !isSelf && (
                        <button className="pw-btn pw-btn-c4" onClick={() => { onC4Invite(target.name); onClose(); }}>
                            🔴 CHALLENGE
                        </button>
                    )}
                </div>
                <div className="pw-pm-row">
                    <input
                        className="pw-pm-input"
                        type="text"
                        placeholder={`Message ${target.name}…`}
                        value={pmText}
                        onChange={e => setPmText(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter' && pmText.trim()) {
                                onAction({ cmd: 'pm', to: target.name, m: pmText.trim() });
                                onPm(target.name);
                                setPmText('');
                            }
                        }}
                        disabled={isSelf}
                    />
                    <button className="pw-btn pw-btn-pm"
                            disabled={isSelf || !pmText.trim()}
                            onClick={() => {
                                onAction({ cmd: 'pm', to: target.name, m: pmText.trim() });
                                onPm(target.name);
                                setPmText('');
                            }}>
                        PM
                    </button>
                </div>
            </div>

            {showMod && (
                <fieldset className="pw-mod">
                    <legend>Moderator</legend>
                    <div className="pw-mod-row">
                        <input className="pw-mod-input" type="number" min={0} max={1440}
                               value={muteMins} onChange={e => setMuteMins(Number(e.target.value || 0))}
                               aria-label="mute minutes" />
                        <button className="pw-btn pw-btn-mod" onClick={() =>
                            onAction({ cmd: 'mute_user', room, user: target.name, minutes: muteMins })}>
                            MUTE
                        </button>
                        <button className="pw-btn pw-btn-mod" onClick={() =>
                            onAction({ cmd: 'mute_user', room, user: target.name, minutes: 0 })}>
                            UN-MUTE
                        </button>
                        <button className="pw-btn pw-btn-mod" onClick={slap}>
                            SLAP
                        </button>
                    </div>
                    <div className="pw-mod-row">
                        <input className="pw-mod-input" type="number" min={0} max={60 * 24 * 30}
                               value={banMins} onChange={e => setBanMins(Number(e.target.value || 0))}
                               aria-label="ban minutes" />
                        <input className="pw-mod-input pw-mod-reason" type="text" maxLength={120}
                               value={banReason} onChange={e => setBanReason(e.target.value)}
                               placeholder="reason (optional)" />
                        <button className="pw-btn pw-btn-mod pw-btn-danger" onClick={() =>
                            onAction({ cmd: 'ban_user', room, user: target.name, minutes: banMins, reason: banReason || undefined })}>
                            BAN
                        </button>
                    </div>
                </fieldset>
            )}
            {showAdmin && (
                <fieldset className="pw-mod">
                    <legend>Admin</legend>
                    <div className="pw-mod-row">
                        <button className="pw-btn pw-btn-mod" onClick={() => {
                            const m = prompt(`System message to ${target.name} (broadcasts):`);
                            if (m) onAction({ cmd: 'user_mess', room, to_uid: target.id, m });
                        }}>
                            ADMIN DM (broadcasts)
                        </button>
                    </div>
                </fieldset>
            )}
        </Modal>
    );
}
